import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { IoClose } from 'react-icons/io5';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import Pagination from '../../../components/common/Pagination';
import {
  DesktopListOnly, MobileCardActions, MobileCardField, MobileCardGrid,
  MobileCardHeader, MobileCardHeading, MobileCardLabel, MobileCardList,
  MobileCardSelect, MobileCardStatus, MobileCardSubtitle, MobileCardTitle,
  MobileCardValue, MobileListCard, MobileListState,
} from '../../../components/common/AdminMobileList';
import { getAllRealtors, approveUser, rejectUser, updateUserStatus } from '../../../services/memberService';

const STATUS_LABEL = {
  active: '활성', pending: '승인 대기', suspended: '정지', inactive: '비활성',
};
const STATUS_COLOR = { active: '#22C55E', pending: '#F59E0B', inactive: '#9CA3AF', suspended: '#EF4444' };

const PER_PAGE = 20;

const Wrap = styled.div``;

const TopRow = styled.div`
  display: flex; justify-content: space-between; align-items: flex-end;
  margin-bottom: 20px;
  gap: 12px; flex-wrap: wrap;
  @media (max-width: 768px) { align-items: stretch; margin-bottom: 14px; }
`;
const Title = styled.h1` font-size: 18px; font-weight: 600; `;
const CountText = styled.span`
  font-size: 13px; color: ${({ theme }) => theme.colors.textSecondary};
  margin-left: 8px;
`;
const ActionRow = styled.div`
  display: flex; gap: 8px; flex-wrap: wrap;
  @media (max-width: 768px) { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); width: 100%; > button:last-child { grid-column: 1 / -1; } }
`;
const Btn = styled.button`
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px; font-weight: 500;
  border: 1px solid ${({ theme, $variant }) =>
    $variant === 'primary' ? theme.colors.primary : theme.colors.border};
  background: ${({ theme, $variant }) =>
    $variant === 'primary' ? theme.colors.primary : '#fff'};
  color: ${({ $variant }) => ($variant === 'primary' ? '#fff' : '#374151')};
  cursor: pointer;
  white-space: nowrap;
  &:hover { opacity: 0.92; }
  @media (max-width: 768px) { min-height: 44px; padding: 9px 10px; }
`;

const FilterBar = styled.div`
  display: flex; gap: 10px; margin-bottom: 16px;
  flex-wrap: wrap;
  @media (max-width: 768px) { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
`;
const SearchInput = styled.input`
  padding: 8px 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: 13px;
  width: 260px;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
  @media (max-width: 768px) { grid-column: 1 / -1; width: 100%; min-height: 44px; }
`;
const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: 13px;
  background: #fff;
  cursor: pointer;
  @media (max-width: 768px) { width: 100%; min-width: 0; min-height: 44px; }
`;

const BulkBar = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 14px;
  margin-bottom: 12px;
  background: #FFFFFF;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: 8px;
  font-size: 13px;
  @media (max-width: 768px) { align-items: stretch; flex-direction: column; }
`;
const BulkBtns = styled.div` display: flex; gap: 6px; @media (max-width: 768px) { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); } `;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 10px;
  overflow: hidden;

  thead tr { background: #F9FAFB; }
  th, td {
    padding: 12px 14px;
    text-align: left;
    font-size: 13px;
  }
  th {
    font-weight: 500;
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 12px;
    white-space: nowrap;
  }
  th.sortable {
    cursor: pointer;
    user-select: none;
    &:hover { color: ${({ theme }) => theme.colors.primary}; }
  }
  th .sortInd {
    display: inline-block;
    margin-left: 6px;
    color: #C0C7D1;
    font-size: 10px;
    line-height: 1;
    vertical-align: middle;
  }
  th.active .sortInd { color: ${({ theme }) => theme.colors.primary}; }
  tbody tr {
    border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
    cursor: pointer;
    &:hover { background: #F9FAFB; }
  }
`;

const StatusText = styled.span`
  color: ${({ $c }) => $c};
  font-size: 13px;
  font-weight: 500;
`;

const AuthText = styled.span`
  font-size: 12px;
  color: ${({ $ok }) => ($ok ? '#22C55E' : '#9CA3AF')};
  font-weight: 500;
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })` cursor: pointer; `;

const MiniBtn = styled.button`
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  border: none;
  color: #fff;
  cursor: pointer;
  background: ${({ $variant }) => ($variant === 'danger' ? '#EF4444' : '#4A6CF7')};
  &:hover { opacity: 0.9; }
`;

// 모달
const Overlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000;
`;
const Modal = styled.div`
  position: relative;
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  width: 100%; max-width: 480px;
`;
const CloseIcon = styled.button`
  position: absolute; top: 14px; right: 14px;
  background: none; border: none; padding: 4px;
  cursor: pointer; color: #6B7280;
  display: inline-flex; align-items: center; justify-content: center;
  &:hover { color: #1F2937; }
`;
const Note = styled.p`
  display: flex; gap: 8px; align-items: flex-start;
  margin: 12px 0 0;
  padding: 10px 12px;
  background: #FEF3C7;
  border: 1px solid #FCD34D;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.5;
  color: #92400E;

  &::before {
    content: '!';
    flex: 0 0 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px; height: 18px;
    background: #92400E;
    color: #fff;
    border-radius: 50%;
    font-weight: 700;
    font-size: 12px;
  }
`;
const ModalTitle = styled.h3` font-size: 16px; font-weight: 600; margin-bottom: 16px; `;
const Field = styled.div` margin-bottom: 12px; `;
const Label = styled.label`
  display: block; font-size: 12px; color: #6B7280; margin-bottom: 4px;
`;
const Input = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: 13px;
`;
const ModalFooter = styled.div`
  display: flex; justify-content: flex-end; gap: 8px;
  margin-top: 16px;
`;

const formatDate = (v) => {
  if (!v) return '-';
  if (typeof v === 'string') return v.slice(0, 10);
  try { return new Date(v).toISOString().slice(0, 10); } catch { return '-'; }
};

const downloadCSV = (rows, filename) => {
  const csv = rows.map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const RealtorList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');

  useEffect(() => {
    const s = searchParams.get('status');
    if (s) setStatusFilter(s);
  }, [searchParams]);

  const [authFilter, setAuthFilter] = useState('all');
  const [activityFilter, setActivityFilter] = useState('all');
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(new Set());
  const [showInvite, setShowInvite] = useState(false);

  const loadUsers = useCallback(async () => {
    setListLoading(true);
    try {
      const list = await getAllRealtors();
      setUsers(list);
    } catch (err) { console.error(err); }
    finally { setListLoading(false); }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const filtered = useMemo(() => {
    let list = users.filter((u) => {
      if (statusFilter !== 'all' && u.status !== statusFilter) return false;
      if (authFilter === 'verified' && !u.accountVerified) return false;
      if (authFilter === 'unverified' && u.accountVerified) return false;
      if (activityFilter === 'active_recent') {
        // 30일 이내 가입 or activity (없으면 가입일 기준)
        const ts = typeof u.createdAt === 'string' ? new Date(u.createdAt.replace(' ', 'T')).getTime() : 0;
        if (Date.now() - ts > 30 * 86400000) return false;
      }
      if (activityFilter === 'inactive') {
        const ts = typeof u.createdAt === 'string' ? new Date(u.createdAt.replace(' ', 'T')).getTime() : 0;
        if (Date.now() - ts <= 90 * 86400000) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        const office = (u.businessName || u.business_name || '').toLowerCase();
        const rep = (u.name || '').toLowerCase();
        const phone = (u.phone || '').toLowerCase();
        if (!office.includes(q) && !rep.includes(q) && !phone.includes(q)) return false;
      }
      return true;
    });
    const STATUS_ORDER = { pending: 0, active: 1, suspended: 2, inactive: 3 };
    const getVal = (u) => {
      if (sortKey === 'name') return (u.businessName || u.business_name || '').toLowerCase();
      if (sortKey === 'status') return STATUS_ORDER[u.status] ?? 99;
      if (sortKey === 'auth') return u.accountVerified ? 0 : 1;
      return typeof u.createdAt === 'string' ? u.createdAt : '';
    };
    list.sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [users, statusFilter, authFilter, activityFilter, sortKey, sortDir, search]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'createdAt' ? 'desc' : 'asc');
    }
    setPage(1);
  };

  const sortInd = (key) => {
    if (sortKey !== key) return <span className="sortInd">▲▼</span>;
    return <span className="sortInd">{sortDir === 'asc' ? '▲' : '▼'}</span>;
  };

  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const toggleOne = (e, uid) => {
    e.stopPropagation();
    setSelected((prev) => {
      const s = new Set(prev);
      s.has(uid) ? s.delete(uid) : s.add(uid);
      return s;
    });
  };
  const toggleAll = (e) => {
    e.stopPropagation();
    if (selected.size === paged.length) setSelected(new Set());
    else setSelected(new Set(paged.map((u) => u.uid)));
  };

  const handleApprove = async (e, uid) => {
    e.stopPropagation();
    if (!window.confirm('이 계정을 승인하시겠습니까?')) return;
    try { await approveUser(uid); loadUsers(); }
    catch (err) { alert(err.message || '승인 실패'); }
  };
  const handleReject = async (e, uid) => {
    e.stopPropagation();
    if (!window.confirm('이 계정을 거절하시겠습니까?')) return;
    try { await rejectUser(uid); loadUsers(); }
    catch (err) { alert(err.message || '거절 실패'); }
  };

  const handleExcel = () => {
    const target = selected.size > 0 ? filtered.filter((u) => selected.has(u.uid)) : filtered;
    const header = ['사무소명', '대표자', '연락처', '이메일', '계좌인증', '등록일', '상태'];
    const rows = target.map((u) => [
      u.businessName || u.business_name || '',
      u.name || '',
      u.phone || '',
      u.email || '',
      u.accountVerified ? '인증완료' : '미인증',
      formatDate(u.createdAt),
      STATUS_LABEL[u.status] || u.status,
    ]);
    downloadCSV([header, ...rows], `공인중개사_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const handleBulkStatus = async (status) => {
    if (selected.size === 0) return alert('선택된 항목이 없습니다.');
    if (!window.confirm(`선택한 ${selected.size}건을 "${STATUS_LABEL[status]}" 로 변경하시겠습니까?`)) return;
    try {
      await Promise.all([...selected].map((uid) => updateUserStatus(uid, status)));
      setSelected(new Set());
      loadUsers();
    } catch (err) { alert(err.message || '변경 실패'); }
  };

  const handleQrGenerate = () => {
    if (selected.size === 0) return alert('QR을 생성할 부동산을 선택하세요.');
    if (selected.size > 1) return alert('한 명씩 선택해 주세요.');
    const uid = [...selected][0];
    navigate(`/admin/members/realtors/${uid}`);
  };

  const allSelected = paged.length > 0 && paged.every((u) => selected.has(u.uid));

  return (
    <Wrap>
      <TopRow>
        <div>
          <Title>공인중개사 관리<CountText>총 {users.length}명 등록</CountText></Title>
        </div>
        <ActionRow>
          <Btn onClick={handleQrGenerate}>QR 코드 생성</Btn>
          <Btn onClick={() => setShowInvite(true)}>공인중개사 초대</Btn>
          <Btn onClick={handleExcel}>엑셀 다운로드</Btn>
        </ActionRow>
      </TopRow>

      <FilterBar>
        <SearchInput
          placeholder="사무소명 / 대표자 / 연락처 검색"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        <Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="all">상태 전체</option>
          <option value="active">활성</option>
          <option value="pending">승인 대기</option>
          <option value="suspended">정지</option>
          <option value="inactive">비활성</option>
        </Select>
        <Select value={authFilter} onChange={(e) => { setAuthFilter(e.target.value); setPage(1); }}>
          <option value="all">계좌인증 전체</option>
          <option value="verified">인증 완료</option>
          <option value="unverified">미인증</option>
        </Select>
        <Select value={activityFilter} onChange={(e) => { setActivityFilter(e.target.value); setPage(1); }}>
          <option value="all">활동 전체</option>
          <option value="active_recent">최근 활동 (30일)</option>
          <option value="inactive">비활동 (90일↑)</option>
        </Select>
      </FilterBar>

      {selected.size > 0 && (
        <BulkBar>
          <span><strong>{selected.size}건 선택됨</strong></span>
          <BulkBtns>
            <Btn onClick={handleExcel}>선택항목 엑셀</Btn>
            <Btn onClick={() => handleBulkStatus('suspended')}>일괄 정지</Btn>
            <Btn onClick={() => handleBulkStatus('active')}>일괄 활성</Btn>
            <Btn onClick={() => setSelected(new Set())}>선택 해제</Btn>
          </BulkBtns>
        </BulkBar>
      )}

      <MobileCardList aria-label="공인중개사 카드 목록">
        {paged.map((u) => {
          const detailPath = `/admin/members/realtors/${u.uid}`;
          return (
            <MobileListCard
              key={u.uid}
              $clickable
              role="button"
              tabIndex={0}
              onClick={() => navigate(detailPath)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  navigate(detailPath);
                }
              }}
            >
              <MobileCardHeader>
                <MobileCardHeading>
                  <MobileCardTitle>{u.businessName || u.business_name || '-'}</MobileCardTitle>
                  <MobileCardSubtitle>대표자 {u.name || '-'}</MobileCardSubtitle>
                </MobileCardHeading>
                <MobileCardStatus><StatusText $c={STATUS_COLOR[u.status] || '#9CA3AF'}>{STATUS_LABEL[u.status] || u.status}</StatusText></MobileCardStatus>
              </MobileCardHeader>
              <MobileCardGrid>
                <MobileCardField><MobileCardLabel>연락처</MobileCardLabel><MobileCardValue>{u.phone || '-'}</MobileCardValue></MobileCardField>
                <MobileCardField><MobileCardLabel>계좌 인증</MobileCardLabel><MobileCardValue><AuthText $ok={!!u.accountVerified}>{u.accountVerified ? '인증완료' : '미인증'}</AuthText></MobileCardValue></MobileCardField>
                <MobileCardField $wide><MobileCardLabel>이메일</MobileCardLabel><MobileCardValue>{u.email || '-'}</MobileCardValue></MobileCardField>
                <MobileCardField><MobileCardLabel>등록일</MobileCardLabel><MobileCardValue>{formatDate(u.createdAt)}</MobileCardValue></MobileCardField>
              </MobileCardGrid>
              <MobileCardActions onClick={(event) => event.stopPropagation()}>
                <MobileCardSelect>
                  <Checkbox checked={selected.has(u.uid)} onChange={(event) => toggleOne(event, u.uid)} />
                  선택
                </MobileCardSelect>
                {u.status === 'pending' ? (
                  <>
                    <MiniBtn onClick={(event) => handleApprove(event, u.uid)}>승인</MiniBtn>
                    <MiniBtn $variant="danger" onClick={(event) => handleReject(event, u.uid)}>거절</MiniBtn>
                  </>
                ) : (
                  <MiniBtn onClick={() => navigate(detailPath)}>상세 보기</MiniBtn>
                )}
              </MobileCardActions>
            </MobileListCard>
          );
        })}
        {listLoading ? <MobileListState>로딩 중...</MobileListState> : paged.length === 0 && <MobileListState>해당 조건의 회원이 없습니다.</MobileListState>}
      </MobileCardList>

      <DesktopListOnly>
      <Table>
        <thead>
          <tr>
            <th style={{ width: 40 }}><Checkbox checked={allSelected} onChange={toggleAll} /></th>
            <th
              className={`sortable${sortKey === 'name' ? ' active' : ''}`}
              onClick={() => toggleSort('name')}
            >사무소명{sortInd('name')}</th>
            <th>대표자</th>
            <th>연락처</th>
            <th>이메일</th>
            <th
              className={`sortable${sortKey === 'auth' ? ' active' : ''}`}
              onClick={() => toggleSort('auth')}
            >계좌인증{sortInd('auth')}</th>
            <th
              className={`sortable${sortKey === 'createdAt' ? ' active' : ''}`}
              onClick={() => toggleSort('createdAt')}
            >등록일{sortInd('createdAt')}</th>
            <th
              className={`sortable${sortKey === 'status' ? ' active' : ''}`}
              onClick={() => toggleSort('status')}
            >상태{sortInd('status')}</th>
            <th>관리</th>
          </tr>
        </thead>
        <tbody>
          {paged.map((u) => (
            <tr key={u.uid} onClick={() => navigate(`/admin/members/realtors/${u.uid}`)}>
              <td onClick={(e) => e.stopPropagation()}>
                <Checkbox checked={selected.has(u.uid)} onChange={(e) => toggleOne(e, u.uid)} />
              </td>
              <td style={{ fontWeight: 500 }}>{u.businessName || u.business_name || '-'}</td>
              <td>{u.name || '-'}</td>
              <td>{u.phone || '-'}</td>
              <td style={{ color: '#6B7280' }}>{u.email || '-'}</td>
              <td><AuthText $ok={!!u.accountVerified}>{u.accountVerified ? '인증완료' : '미인증'}</AuthText></td>
              <td style={{ color: '#6B7280' }}>{formatDate(u.createdAt)}</td>
              <td><StatusText $c={STATUS_COLOR[u.status] || '#9CA3AF'}>{STATUS_LABEL[u.status] || u.status}</StatusText></td>
              <td onClick={(e) => e.stopPropagation()}>
                {u.status === 'pending' ? (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <MiniBtn onClick={(e) => handleApprove(e, u.uid)}>승인</MiniBtn>
                    <MiniBtn $variant="danger" onClick={(e) => handleReject(e, u.uid)}>거절</MiniBtn>
                  </div>
                ) : (
                  <MiniBtn onClick={(e) => { e.stopPropagation(); navigate(`/admin/members/realtors/${u.uid}`); }}>상세</MiniBtn>
                )}
              </td>
            </tr>
          ))}
          {listLoading ? (
            <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40 }}>로딩 중...</td></tr>
          ) : paged.length === 0 && (
            <tr><td colSpan={9} style={{ textAlign: 'center', color: '#9CA3AF', padding: 32 }}>해당 조건의 회원이 없습니다.</td></tr>
          )}
        </tbody>
      </Table>
      </DesktopListOnly>

      <Pagination current={page} total={Math.max(1, Math.ceil(filtered.length / PER_PAGE))} onChange={setPage} />

      {showInvite && <InviteModal onClose={() => setShowInvite(false)} />}
    </Wrap>
  );
};

const InviteModal = ({ onClose }) => {
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [referrer, setReferrer] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    const cleanPhone = phone.replace(/\D/g, '');
    if (!cleanPhone || cleanPhone.length < 10) {
      alert('휴대폰 번호를 정확히 입력하세요.');
      return;
    }
    setLoading(true);
    try {
      await addDoc(collection(db, 'realtor_invitations'), {
        phone: cleanPhone,
        name: name || '',
        referrer: referrer || '',
        status: 'pending',
        created_at: serverTimestamp(),
      });
      alert('초대 요청이 생성됐어요. 발송 대기 중입니다.');
      onClose();
    } catch (err) {
      alert(err.message || '초대 실패');
    } finally { setLoading(false); }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <CloseIcon onClick={onClose} aria-label="닫기"><IoClose size={20} /></CloseIcon>
        <ModalTitle>공인중개사 초대</ModalTitle>
        <Field><Label>휴대폰번호 *</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="010-0000-0000" /></Field>
        <Field><Label>이름 (선택)</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field><Label>추천인 (선택)</Label><Input value={referrer} onChange={(e) => setReferrer(e.target.value)} placeholder="추천인 코드 또는 이름" /></Field>
        <Note>초대 발송은 SMS 발송 대기열에 쌓이며, 관리자 승인이 필요합니다. 기존 세션에는 영향을 주지 않습니다.</Note>
        <ModalFooter>
          <Btn onClick={onClose} disabled={loading}>취소</Btn>
          <Btn $variant="primary" onClick={submit} disabled={loading}>{loading ? '전송 중...' : '초대 발송'}</Btn>
        </ModalFooter>
      </Modal>
    </Overlay>
  );
};

export default RealtorList;
